import type { ReactNode, SVGProps } from "react";

export type IconProps = Omit<SVGProps<SVGSVGElement>, "children">;

/**
 * Line icons of the Steel Zero Trust design (24 px grid, 2 px stroke, `currentColor`), drawn for
 * S-Trust. Decorative by default: the control or text next to an icon carries its meaning.
 */
function createIcon(name: string, paths: ReactNode) {
  function Icon(props: IconProps) {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
        data-icon={name}
        {...props}
      >
        {paths}
      </svg>
    );
  }
  Icon.displayName = `${name}Icon`;
  return Icon;
}

export const ShieldCheckIcon = createIcon(
  "shield-check",
  <>
    <path d="M12 3 4.5 6v5.5c0 4.6 3.1 8.2 7.5 9.5 4.4-1.3 7.5-4.9 7.5-9.5V6L12 3Z" />
    <path d="m9 12 2 2 4-4" />
  </>,
);
export const ShieldIcon = createIcon(
  "shield",
  <path d="M12 3 4.5 6v5.5c0 4.6 3.1 8.2 7.5 9.5 4.4-1.3 7.5-4.9 7.5-9.5V6L12 3Z" />,
);
export const SearchIcon = createIcon(
  "search",
  <>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </>,
);
export const SearchXIcon = createIcon(
  "search-x",
  <>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5M9 9l4 4M13 9l-4 4" />
  </>,
);
export const UsersIcon = createIcon(
  "users",
  <>
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
    <path d="M16 4.6a3.5 3.5 0 0 1 0 6.8M18.5 14.3A6.5 6.5 0 0 1 21.5 20" />
  </>,
);
export const UserIcon = createIcon(
  "user",
  <>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0 1 16 0" />
  </>,
);
export const UserCogIcon = createIcon(
  "user-cog",
  <>
    <circle cx="10" cy="8" r="4" />
    <path d="M3 21a7 7 0 0 1 10-6.3" />
    <circle cx="18" cy="17" r="2.5" />
    <path d="M18 13v1.5M18 19.5V21M14 17h1.5M20.5 17H22" />
  </>,
);
export const UserXIcon = createIcon(
  "user-x",
  <>
    <circle cx="10" cy="8" r="4" />
    <path d="M3 21a7 7 0 0 1 12.5-4.3M17 14l4 4M21 14l-4 4" />
  </>,
);
export const UserPlusIcon = createIcon(
  "user-plus",
  <>
    <circle cx="10" cy="8" r="4" />
    <path d="M3 21a7 7 0 0 1 12.5-4.3M19 13v6M16 16h6" />
  </>,
);
export const BanIcon = createIcon(
  "ban",
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="m5.6 5.6 12.8 12.8" />
  </>,
);
export const MapIcon = createIcon(
  "map",
  <>
    <path d="M9 4 3 6.5v13.5L9 17.5l6 2.5 6-2.5V4l-6 2.5L9 4Z" />
    <path d="M9 4v13.5M15 6.5V20" />
  </>,
);
export const MapPinIcon = createIcon(
  "map-pin",
  <>
    <path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21Z" />
    <circle cx="12" cy="9.5" r="2.5" />
  </>,
);
export const KeyRoundIcon = createIcon(
  "key-round",
  <>
    <circle cx="8" cy="15" r="4.5" />
    <path d="m11.2 11.8 8.3-8.3M16.5 6.5l2.5 2.5M14 9l2 2" />
  </>,
);
export const ScrollTextIcon = createIcon(
  "scroll-text",
  <>
    <path d="M6 3h11a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5" />
    <path d="M5 5a2 2 0 0 0-2 2v1h2M9 8h6M9 12h6M9 16h4" />
  </>,
);
export const BuildingIcon = createIcon(
  "building",
  <>
    <rect x="4" y="3" width="16" height="18" rx="1.5" />
    <path d="M9 21v-4h6v4M8 7h2M14 7h2M8 11h2M14 11h2" />
  </>,
);
export const NetworkIcon = createIcon(
  "network",
  <>
    <rect x="9" y="3" width="6" height="5" rx="1" />
    <rect x="3" y="16" width="6" height="5" rx="1" />
    <rect x="15" y="16" width="6" height="5" rx="1" />
    <path d="M12 8v4M6 16v-2h12v2" />
  </>,
);
export const ChevronRightIcon = createIcon("chevron-right", <path d="m9 6 6 6-6 6" />);
export const ChevronLeftIcon = createIcon("chevron-left", <path d="m15 6-6 6 6 6" />);
export const ChevronDownIcon = createIcon("chevron-down", <path d="m6 9 6 6 6-6" />);
export const ChevronUpIcon = createIcon("chevron-up", <path d="m6 15 6-6 6 6" />);
export const ChevronsLeftIcon = createIcon(
  "chevrons-left",
  <path d="m11 6-6 6 6 6M19 6l-6 6 6 6" />,
);
export const ChevronsRightIcon = createIcon(
  "chevrons-right",
  <path d="m13 6 6 6-6 6M5 6l6 6-6 6" />,
);
export const ArrowDownIcon = createIcon("arrow-down", <path d="M12 5v14M6 13l6 6 6-6" />);
export const ArrowUpIcon = createIcon("arrow-up", <path d="M12 19V5M6 11l6-6 6 6" />);
export const ArrowUpDownIcon = createIcon(
  "arrow-up-down",
  <path d="M8 4v16M4 8l4-4 4 4M16 20V4M12 16l4 4 4-4" />,
);
export const ArrowUpRightIcon = createIcon("arrow-up-right", <path d="M7 17 17 7M8 7h9v9" />);
export const ArrowLeftIcon = createIcon("arrow-left", <path d="M19 12H5M11 6l-6 6 6 6" />);
export const GlobeIcon = createIcon(
  "globe",
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3Z" />
  </>,
);
export const LanguagesIcon = createIcon(
  "languages",
  <>
    <path d="M4 5h9M8.5 3v2M6 5c.6 3.4 2.6 6 5.5 7.5M11 5c-.8 3.8-3 6.6-6.5 8" />
    <path d="m13 21 4-10 4 10M14.5 17.5h5" />
  </>,
);
export const MoonIcon = createIcon(
  "moon",
  <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5Z" />,
);
export const SunIcon = createIcon(
  "sun",
  <>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2.5v2M12 19.5v2M4.6 4.6 6 6M18 18l1.4 1.4M2.5 12h2M19.5 12h2M4.6 19.4 6 18M18 6l1.4-1.4" />
  </>,
);
export const BellIcon = createIcon(
  "bell",
  <>
    <path d="M18 8.5a6 6 0 0 0-12 0c0 6.5-3 8.5-3 8.5h18s-3-2-3-8.5" />
    <path d="M13.7 20.5a2 2 0 0 1-3.4 0" />
  </>,
);
export const LayoutGridIcon = createIcon(
  "layout-grid",
  <>
    <rect x="3.5" y="3.5" width="7" height="7" rx="1" />
    <rect x="13.5" y="3.5" width="7" height="7" rx="1" />
    <rect x="3.5" y="13.5" width="7" height="7" rx="1" />
    <rect x="13.5" y="13.5" width="7" height="7" rx="1" />
  </>,
);
export const CheckIcon = createIcon("check", <path d="m5 12.5 4.5 4.5L19 7" />);
export const XIcon = createIcon("x", <path d="M6 6l12 12M18 6 6 18" />);
export const PlusIcon = createIcon("plus", <path d="M12 5v14M5 12h14" />);
export const MinusIcon = createIcon("minus", <path d="M5 12h14" />);
export const CopyIcon = createIcon(
  "copy",
  <>
    <rect x="8.5" y="8.5" width="12" height="12" rx="2" />
    <path d="M15.5 8.5V5.5a2 2 0 0 0-2-2h-8a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h3" />
  </>,
);
export const DownloadIcon = createIcon("download", <path d="M12 4v11M7 10.5l5 5 5-5M4.5 20h15" />);
export const UploadIcon = createIcon("upload", <path d="M12 16V5M7 9.5l5-5 5 5M4.5 20h15" />);
export const PrinterIcon = createIcon(
  "printer",
  <>
    <path d="M7 9V3.5h10V9M7 17H4.5v-6a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v6H17" />
    <rect x="7" y="14" width="10" height="6.5" rx="1" />
  </>,
);
export const StickyNoteIcon = createIcon(
  "sticky-note",
  <>
    <path d="M15 21H5.5A2.5 2.5 0 0 1 3 18.5v-13A2.5 2.5 0 0 1 5.5 3h13A2.5 2.5 0 0 1 21 5.5V15l-6 6Z" />
    <path d="M15 21v-4.5a1.5 1.5 0 0 1 1.5-1.5H21" />
  </>,
);
export const InfoIcon = createIcon(
  "info",
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5.5M12 7.6v.1" />
  </>,
);
export const AlertTriangleIcon = createIcon(
  "alert-triangle",
  <>
    <path d="M10.3 4.2 2.6 17.6A2 2 0 0 0 4.3 20.6h15.4a2 2 0 0 0 1.7-3L13.7 4.2a2 2 0 0 0-3.4 0Z" />
    <path d="M12 9.5v4M12 17v.1" />
  </>,
);
export const OctagonXIcon = createIcon(
  "octagon-x",
  <>
    <path d="M8.3 2.5h7.4l5.3 5.3v7.4l-5.3 5.3H8.3L3 15.2V7.8l5.3-5.3Z" />
    <path d="m9 9 6 6M15 9l-6 6" />
  </>,
);
export const CircleCheckIcon = createIcon(
  "circle-check",
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="m8 12.5 2.8 2.8L16.5 9.5" />
  </>,
);
export const CircleDashedIcon = createIcon(
  "circle-dashed",
  <path d="M10.1 3.2a9 9 0 0 1 3.8 0M17.6 5.3a9 9 0 0 1 2 2.6M20.8 10.1a9 9 0 0 1 0 3.8M18.7 17.6a9 9 0 0 1-2.6 2M13.9 20.8a9 9 0 0 1-3.8 0M6.4 18.7a9 9 0 0 1-2-2.6M3.2 13.9a9 9 0 0 1 0-3.8M5.3 6.4a9 9 0 0 1 2.6-2" />,
);
export const LockIcon = createIcon(
  "lock",
  <>
    <rect x="4.5" y="10.5" width="15" height="10.5" rx="2" />
    <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3M12 15v2" />
  </>,
);
export const ServerCrashIcon = createIcon(
  "server-crash",
  <>
    <path d="M6 10H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1M6 14H5a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2h-1" />
    <path d="m13 6-4 6h6l-4 6M6.5 6.5v.1M6.5 17.5v.1" />
  </>,
);
export const ActivityIcon = createIcon("activity", <path d="M3 12h4l3-8 4 16 3-8h4" />);
export const MonitorIcon = createIcon(
  "monitor",
  <>
    <rect x="3" y="4" width="18" height="12.5" rx="2" />
    <path d="M8.5 20.5h7M12 16.5v4" />
  </>,
);
export const SmartphoneIcon = createIcon(
  "smartphone",
  <>
    <rect x="6.5" y="2.5" width="11" height="19" rx="2" />
    <path d="M11 18h2" />
  </>,
);
export const CalendarIcon = createIcon(
  "calendar",
  <>
    <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
    <path d="M3.5 10h17M8 3v4M16 3v4" />
  </>,
);
export const ClockIcon = createIcon(
  "clock",
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </>,
);
export const FilterIcon = createIcon("filter", <path d="M3.5 5h17l-6.5 7.5V19l-4 2v-8.5L3.5 5Z" />);
export const ColumnsIcon = createIcon(
  "columns",
  <>
    <rect x="3.5" y="4" width="17" height="16" rx="2" />
    <path d="M9.5 4v16M14.5 4v16" />
  </>,
);
export const ListIcon = createIcon(
  "list",
  <path d="M8.5 6h12M8.5 12h12M8.5 18h12M3.5 6h.1M3.5 12h.1M3.5 18h.1" />,
);
export const LayersIcon = createIcon(
  "layers",
  <>
    <path d="m12 3 9 5-9 5-9-5 9-5Z" />
    <path d="m3 13 9 5 9-5" />
  </>,
);
export const MaximizeIcon = createIcon(
  "maximize",
  <path d="M8 3.5H3.5V8M16 3.5h4.5V8M3.5 16v4.5H8M20.5 16v4.5H16" />,
);
export const RulerIcon = createIcon(
  "ruler",
  <>
    <path d="m15.5 2.8 5.7 5.7L8.5 21.2l-5.7-5.7L15.5 2.8Z" />
    <path d="m12 6.3 2 2M9.2 9.1l2 2M6.4 11.9l2 2" />
  </>,
);
export const EraserIcon = createIcon(
  "eraser",
  <>
    <path d="m7 21-4.3-4.3a2.4 2.4 0 0 1 0-3.4l9.6-9.6a2.4 2.4 0 0 1 3.4 0l5.6 5.6a2.4 2.4 0 0 1 0 3.4L13 21" />
    <path d="M22 21H7" />
    <path d="m5 11 9 9" />
  </>,
);
export const SquareIcon = createIcon(
  "square",
  <rect x="4" y="4" width="16" height="16" rx="1.5" />,
);
export const HexagonIcon = createIcon(
  "hexagon",
  <path d="m12 2.5 8.5 4.9v9.2L12 21.5l-8.5-4.9V7.4L12 2.5Z" />,
);
export const ExternalLinkIcon = createIcon(
  "external-link",
  <path d="M14 4h6v6M20 4l-9 9M18 14v5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 4 19V7.5A1.5 1.5 0 0 1 5.5 6H10" />,
);
export const LogOutIcon = createIcon(
  "log-out",
  <path d="M9.5 20.5H5.5a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2h4M16 16.5l4.5-4.5L16 7.5M20.5 12H9.5" />,
);
export const LogInIcon = createIcon(
  "log-in",
  <path d="M14.5 3.5h4a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2h-4M8 7.5 12.5 12 8 16.5M12.5 12h-9" />,
);
export const CircleXIcon = createIcon(
  "circle-x",
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="m9 9 6 6M15 9l-6 6" />
  </>,
);
export const MailIcon = createIcon(
  "mail",
  <>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3.5 6.5 8.5 6.5 8.5-6.5" />
  </>,
);
export const RefreshIcon = createIcon(
  "refresh",
  <path d="M20 11a8 8 0 0 0-14.3-4.6L4 8M4 4v4h4M4 13a8 8 0 0 0 14.3 4.6L20 16M20 20v-4h-4" />,
);
export const PanelLeftIcon = createIcon(
  "panel-left",
  <>
    <rect x="3.5" y="4" width="17" height="16" rx="2" />
    <path d="M9.5 4v16" />
  </>,
);
export const FingerprintIcon = createIcon(
  "fingerprint",
  <path d="M6.5 18.5A9.5 9.5 0 0 1 5 12a7 7 0 0 1 14 0v1.5M9 21a12 12 0 0 1-1-5v-4a4 4 0 0 1 8 0v4.5M12 12v4a10 10 0 0 0 1.5 5.5M16 20a14 14 0 0 0 .8-2.5" />,
);
export const WifiIcon = createIcon(
  "wifi",
  <path d="M2.5 9a14 14 0 0 1 19 0M5.5 12.5a9.5 9.5 0 0 1 13 0M8.8 16a4.5 4.5 0 0 1 6.4 0M12 19.5v.1" />,
);
export const ServerIcon = createIcon(
  "server",
  <>
    <rect x="3" y="3.5" width="18" height="7" rx="2" />
    <rect x="3" y="13.5" width="18" height="7" rx="2" />
    <path d="M7 7h.1M7 17h.1" />
  </>,
);
export const EyeIcon = createIcon(
  "eye",
  <>
    <path d="M2.5 12S6 5 12 5s9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7Z" />
    <circle cx="12" cy="12" r="3" />
  </>,
);
export const EyeOffIcon = createIcon(
  "eye-off",
  <path d="M9.9 5.2A9.7 9.7 0 0 1 12 5c6 0 9.5 7 9.5 7a17 17 0 0 1-2.4 3.3M6.6 6.6C4 8.2 2.5 12 2.5 12s3.5 7 9.5 7a9.5 9.5 0 0 0 5.4-1.6M3 3l18 18M9.9 9.9a3 3 0 0 0 4.2 4.2" />,
);
export const PencilIcon = createIcon(
  "pencil",
  <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7.5 18.5 3.5 19.5l1-4 12-12ZM14.5 5.5l3 3" />,
);
export const TrashIcon = createIcon(
  "trash",
  <path d="M4 6.5h16M9 6.5V4.5h6v2M6 6.5l1 13.5h10l1-13.5M10 10.5v6M14 10.5v6" />,
);
export const MoreHorizontalIcon = createIcon(
  "more-horizontal",
  <path d="M6 12h.1M12 12h.1M18 12h.1" />,
);
export const PlayIcon = createIcon("play", <path d="M7 4.5v15L19 12 7 4.5Z" />);
export const PowerIcon = createIcon("power", <path d="M12 3v8M6.4 6.4a8 8 0 1 0 11.2 0" />);
export const UnlockIcon = createIcon(
  "unlock",
  <>
    <rect x="4.5" y="10.5" width="15" height="10.5" rx="2" />
    <path d="M8 10.5V7.5a4 4 0 0 1 7.7-1.5" />
  </>,
);
export const SendIcon = createIcon(
  "send",
  <path d="M21 3 10.5 13.5M21 3l-6.5 18-4-7.5L3 9.5 21 3Z" />,
);
export const AtSignIcon = createIcon(
  "at-sign",
  <>
    <circle cx="12" cy="12" r="4" />
    <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8" />
  </>,
);
export const SaveIcon = createIcon(
  "save",
  <>
    <path d="M5 3h11l4 4v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
    <path d="M8 3v5h7V3M7 21v-7h10v7" />
  </>,
);
export const LinkIcon = createIcon(
  "link",
  <path d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1 1M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1-1" />,
);
export const HomeIcon = createIcon(
  "home",
  <path d="M3.5 10.5 12 3.5l8.5 7V20a1 1 0 0 1-1 1H15v-6h-6v6H4.5a1 1 0 0 1-1-1v-9.5Z" />,
);
